/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/


import React, { useMemo } from 'react';
import { motion } from 'framer-motion';

const StarField = () => {
    // Reduced star cobnt for performance
    const stars = useMemo(() => {
        return Array.from({ length: 15 }).map((_, i) => ({
            id: i,
            size: Math.random() * 2 + 1,
            x: Math.random() * 100,
            y: Math.random() * 100,
            duration: Math.random() * 3 + 2,
            delay: Math.random() * 2,
            opacity: Math.random() * 0.7 + 0.3
        }));
    }, []);

    return (
        <div className="absolute inset-0 z-0 pointer-events-none">
            {stars.map((star) => (
                <motion.div
                    key={star.id}
                    className="absolute rounded-full bg-white will-change-[opacity,transform]"
                    style={{
                        left: `${star.x}%`,
                        top: `${star.y}%`,
                        width: star.size,
                        height: star.size,
                        transform: 'translateZ(0)'
                    }}
                    initial={{ opacity: star.opacity, scale: 1 }}
                    animate={{
                        opacity: [star.opacity, 1, star.opacity],
                        scale: [1, 1.5, 1],
                    }}
                    transition={{
                        duration: star.duration * 2, // Slower animation
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: star.delay,
                    }}
                />
            ))}
        </div>
    );
};

const HatsBackground = () => {
    const hats = useMemo(() => {
        return Array.from({ length: 30 }).map((_, i) => ({
            id: i,
            size: Math.random() * 60 + 30, // Random sizes between 30px and 90px
            x: Math.random() * 100,
            y: Math.random() * 100,
            rotation: Math.random() * 360,
            durationY: Math.random() * 20 + 30,
            durationX: Math.random() * 20 + 30,
            durationR: Math.random() * 25 + 35,
            delay: Math.random() * -40,
            opacity: Math.random() * 0.1 + 0.05 // Opacity between 0.05 and 0.15
        }));
    }, []);

    return (
        <div className="absolute inset-0 z-[1] pointer-events-none overflow-hidden">
            {hats.map((hat) => (
                <motion.img
                    key={hat.id}
                    src="/chapeau.png"
                    alt=""
                    className="absolute will-change-transform"
                    style={{
                        left: `${hat.x}%`,
                        top: `${hat.y}%`,
                        width: hat.size,
                        height: 'auto',
                        opacity: hat.opacity,
                    }}
                    animate={{
                        y: ['-8vh', '8vh', '-8vh'],
                        x: ['-5vw', '5vw', '-5vw'],
                        rotate: [hat.rotation, hat.rotation + 45, hat.rotation - 15, hat.rotation],
                    }}
                    transition={{
                        y: { duration: hat.durationY, repeat: Infinity, ease: "easeInOut", delay: hat.delay },
                        x: { duration: hat.durationX, repeat: Infinity, ease: "easeInOut", delay: hat.delay },
                        rotate: { duration: hat.durationR, repeat: Infinity, ease: "linear", delay: hat.delay },
                    }}
                />
            ))}
        </div>
    );
};

const FluidBackground: React.FC = () => {
    return (
        <div className="fixed inset-0 -z-10 overflow-hidden bg-gradient-to-br from-[#31326f] via-[#28295c] to-[#1f2048]">

            <StarField />

            <HatsBackground />

            {/* Blob 1: Mint/Vert clair */}
            <motion.div
                className="absolute top-[-10%] left-[-10%] w-[90vw] h-[90vw] bg-[#6fe1a3] rounded-full mix-blend-screen filter blur-[40px] opacity-30 will-change-transform"
                animate={{ x: [0, 50, -25, 0], y: [0, -25, 25, 0] }}
                transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                style={{ transform: 'translateZ(0)' }}
            />

            {/* Blob 2: Teal → Vert moyen */}
            <motion.div
                className="absolute top-[20%] right-[-20%] w-[100vw] h-[80vw] bg-[#3ec08c] rounded-full mix-blend-screen filter blur-[40px] opacity-20 will-change-transform"
                animate={{ x: [0, -50, 25, 0], y: [0, 50, -25, 0] }}
                transition={{ duration: 30, repeat: Infinity, ease: "easeInOut" }}
                style={{ transform: 'translateZ(0)' }}
            />

            {/* Blob 3: Periwinkle → Vert doux */}
            <motion.div
                className="absolute bottom-[-20%] left-[20%] w-[80vw] h-[80vw] bg-[#4fb77c] rounded-full mix-blend-screen filter blur-[40px] opacity-20 will-change-transform"
                animate={{ x: [0, 75, -75, 0], y: [0, -50, 50, 0] }}
                transition={{ duration: 35, repeat: Infinity, ease: "easeInOut" }}
                style={{ transform: 'translateZ(0)' }}
            />
            {/* Static Grain Overlay */}
            <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10 mix-blend-overlay pointer-events-none"></div>

            {/* Vignette */}
            <div className="absolute inset-0 bg-radial-gradient from-transparent via-black/10 to-black/60 pointer-events-none" />
        </div>
    );
};

export default FluidBackground;